import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Custom Kitchen",
  "Wardrobe / Dressing Room",
  "Living Space Cabinetry",
  "Wall Panels",
  "Bathroom Vanity",
  "Custom CNC / Prototyping",
  "Flatpack / DIY",
  "Full Home Joinery",
  "Other",
];

const formatError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_type) {
      toast.error("Please select a project type.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatError(data.detail));
      }
      toast.success("Thank you — your enquiry is with our workshop. We'll be in touch within one working day.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      toast.error(err.message || "Could not send your enquiry. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "bg-espresso-800 border-espresso-600 text-cream placeholder:text-cream-muted/50 rounded-none focus-visible:ring-brass focus-visible:border-brass h-12";

  return (
    <section id="quote" data-testid="quote-section" className="py-24 lg:py-32 bg-espresso-900 grain relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-2"
        >
          <p className="section-label" data-testid="quote-label">Request a Quote</p>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-cream mt-4" data-testid="quote-heading">
            Tell us about <span className="italic text-brass">your project</span>
          </h2>
          <p className="text-sm text-cream-muted leading-relaxed mt-6 max-w-md">
            Share a few details and we'll arrange a free home consultation — measurements,
            material samples and a detailed quotation, all without obligation.
          </p>
          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <MapPin size={18} className="text-brass shrink-0" />
              <p className="text-sm text-cream-muted">10/15 Icon Drive, Delacombe, VIC 3356</p>
            </div>
            <div className="flex items-center gap-4">
              <Mail size={18} className="text-brass shrink-0" />
              <a href="mailto:info@goldenmileinteriors.com.au" data-testid="quote-email-link" className="text-sm text-cream-muted hover:text-brass transition-colors">
                info@goldenmileinteriors.com.au
              </a>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="quote-form"
          className="lg:col-span-3 border border-espresso-600 bg-espresso-800/60 p-8 lg:p-12 space-y-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="quote-name" className="text-xs tracking-[0.15em] uppercase text-cream-muted">Name *</Label>
              <Input id="quote-name" data-testid="quote-form-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-email" className="text-xs tracking-[0.15em] uppercase text-cream-muted">Email *</Label>
              <Input id="quote-email" data-testid="quote-form-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="quote-phone" className="text-xs tracking-[0.15em] uppercase text-cream-muted">Phone</Label>
              <Input id="quote-phone" data-testid="quote-form-phone" value={form.phone} onChange={set("phone")} placeholder="Optional" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase text-cream-muted">Project Type *</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                <SelectTrigger data-testid="quote-form-project-type" className="bg-espresso-800 border-espresso-600 text-cream rounded-none focus:ring-brass h-12">
                  <SelectValue placeholder="Select a project type" />
                </SelectTrigger>
                <SelectContent className="bg-espresso-800 border-espresso-600 text-cream rounded-none">
                  {PROJECT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`quote-form-type-${t.toLowerCase().replace(/[\s/]+/g, "-")}`} className="focus:bg-espresso-700 focus:text-brass">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="quote-message" className="text-xs tracking-[0.15em] uppercase text-cream-muted">About Your Project *</Label>
            <Textarea
              id="quote-message"
              data-testid="quote-form-message"
              required
              minLength={10}
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Room dimensions, styles you like, timescales — anything that helps us understand the brief."
              className="bg-espresso-800 border-espresso-600 text-cream placeholder:text-cream-muted/50 rounded-none focus-visible:ring-brass focus-visible:border-brass"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="quote-form-submit"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brass text-espresso-900 font-semibold px-10 py-4 hover:bg-brass-hover transition-colors disabled:opacity-60"
          >
            {sending && <Loader2 size={16} className="animate-spin" />}
            {sending ? "Sending…" : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
